import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { Button, Input, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { DateRangePicker as ZhDateRangePicker, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { AdminDetailTable } from "@/views/components/DetailTable";

export interface CustomerLogisticsRecord {
    transportOrderShip?: {
        id?: number | string;
        type?: number | string;
        ownerEnterprise?: { name?: string; realName?: string };
        sourcePortName?: string;
        destinationPortName?: string;
        shipName?: string;
        upstreamHandlerName?: string;
        downstreamHandlerName?: string;
        unitPrice?: number | string;
        ton?: number | string;
        actualFreightCost?: number | string;
        createdTime?: string;
        state?: number | string;
    };
}

export interface CustomerLogisticsQuery {
    customerId: number;
    page: number;
    size: number;
    keyword?: string;
    type?: number;
    portKeyword?: string;
    shipKeyword?: string;
    handlerName?: string;
    state?: number;
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface CustomerLogisticsPageProps {
    customerId: number;
    loadLogistics?: (query: CustomerLogisticsQuery) => Promise<{ list?: CustomerLogisticsRecord[]; total?: number }>;
}

const typeLabels: Record<number, string> = { 1: "平台创建", 2: "船主报价", 3: "船代报价" };
const stateLabels: Record<number, string> = {
    3: "待发航",
    4: "运输中",
    5: "待卸货",
    8: "已完成",
    10: "待付定金",
    13: "未完成"
};
/** 复用 Vue `/admin/transport/order/ship/paging` 的用户物流列表接口。 */
export async function loadCustomerLogistics(query: CustomerLogisticsQuery) {
    const response = await request.service.get("/admin/transport/order/ship/paging", { params: query });
    return response.data as { list?: CustomerLogisticsRecord[]; total?: number };
}

/** 将 zhihao-ui-react 的创建时间排序方向转换为 Vue 运单接口参数。 */
export function getCustomerLogisticsSortParams({
    prop,
    order
}: {
    prop?: string;
    order: "ascending" | "descending" | null;
}): Pick<CustomerLogisticsQuery, "sortKey" | "sortOrder"> {
    if (prop !== "createdTime" || !order) return {};
    return {
        sortKey: "created_time",
        sortOrder: order === "ascending" ? "asc" : "desc"
    };
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE);

/** 保留 Vue 物流列表的实际运费优先、单价乘吨回退计算口径。 */
const formatFreight = (row: CustomerLogisticsRecord) => {
    const ship = row.transportOrderShip;
    const actualFreightCost = Number(ship?.actualFreightCost || 0);
    const freight = actualFreightCost || Number(ship?.unitPrice || 0) * Number(ship?.ton || 0);
    return freight.toFixed(2);
};

export function CustomerLogisticsPage({
    customerId,
    loadLogistics = loadCustomerLogistics
}: CustomerLogisticsPageProps) {
    const [rows, setRows] = useState<CustomerLogisticsRecord[]>([]);
    const [total, setTotal] = useState(0);
    // 对齐旧 Vue ProTable 的分页状态，确保翻页参数进入 waybill paging 请求。
    const [page, setPage] = useState(1);
    const [keyword, setKeyword] = useState("");
    const [type, setType] = useState<number>();
    const [portKeyword, setPortKeyword] = useState("");
    const [shipKeyword, setShipKeyword] = useState("");
    const [handlerName, setHandlerName] = useState("");
    const [state, setState] = useState<number>();
    const [beginTime, setBeginTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [submittedQuery, setSubmittedQuery] = useState<Partial<CustomerLogisticsQuery>>({});
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        const query: CustomerLogisticsQuery = {
            customerId,
            page,
            size: 10,
            ...submittedQuery
        };
        void loadLogistics(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setErrorMessage("物流信息加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [customerId, loadLogistics, page, submittedQuery]);

    const submitSearch = () => {
        // 只提交有值的筛选条件，避免空字符串污染 Vue 分页接口参数。
        const nextQuery: Partial<CustomerLogisticsQuery> = {};
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (type != null) nextQuery.type = type;
        if (portKeyword.trim()) nextQuery.portKeyword = portKeyword.trim();
        if (shipKeyword.trim()) nextQuery.shipKeyword = shipKeyword.trim();
        if (handlerName.trim()) nextQuery.handlerName = handlerName.trim();
        if (state != null) nextQuery.state = state;
        if (beginTime) nextQuery.beginTime = `${beginTime} 00:00:00`;
        if (endTime) nextQuery.endTime = `${endTime} 23:59:59`;
        setSubmittedQuery(nextQuery);
    };

    /** 将旧 Vue 创建时间范围映射为 waybill paging 的整日边界。 */
    const updateCreatedTimeRange = (_value: unknown, formattedValue: [string, string] | null) => {
        setBeginTime(formattedValue?.[0] || "");
        setEndTime(formattedValue?.[1] || "");
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 船运单列表的嵌套字段和展示口径。
    const columns: TableColumnProps<CustomerLogisticsRecord>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 176,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.id ?? DEFAULT_VALUE
        },
        {
            key: "type",
            label: "类型",
            minWidth: 108,
            prop: "transportOrderShip",
            render: ({ row }) => typeLabels[Number(row.transportOrderShip?.type)] || DEFAULT_VALUE
        },
        {
            key: "owner",
            label: "货主",
            minWidth: 120,
            prop: "transportOrderShip",
            render: ({ row }) =>
                row.transportOrderShip?.ownerEnterprise?.name ||
                row.transportOrderShip?.ownerEnterprise?.realName ||
                DEFAULT_VALUE
        },
        {
            key: "sourcePortName",
            label: "始发地",
            minWidth: 148,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.sourcePortName || DEFAULT_VALUE
        },
        {
            key: "destinationPortName",
            label: "目的地",
            minWidth: 148,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.destinationPortName || DEFAULT_VALUE
        },
        {
            key: "shipName",
            label: "船舶",
            minWidth: 204,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.shipName?.split("/")[0] || DEFAULT_VALUE
        },
        {
            key: "upstreamHandlerName",
            label: "上游专员",
            minWidth: 120,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.upstreamHandlerName || DEFAULT_VALUE
        },
        {
            key: "downstreamHandlerName",
            label: "下游专员",
            minWidth: 120,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.downstreamHandlerName || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            minWidth: 188,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatDate(row.transportOrderShip?.createdTime)
        },
        {
            key: "state",
            label: "状态",
            width: 188,
            prop: "transportOrderShip",
            render: ({ row }) => stateLabels[Number(row.transportOrderShip?.state)] || DEFAULT_VALUE
        },
        {
            key: "freight",
            label: "运费",
            prop: "transportOrderShip",
            render: ({ row }) => formatFreight(row)
        }
    ];

    return (
        <section aria-label="customer-logistics-page" className="section-card">
            <AdminDetailTable<CustomerLogisticsRecord>
                data={rows}
                columns={columns}
                pagination={false}
                rowKey={row => String(row.transportOrderShip?.id || "")}
                onSortChange={sort => {
                    setPage(1);
                    setSubmittedQuery(current => {
                        const nextQuery = { ...current };
                        const sortParams = getCustomerLogisticsSortParams(sort);
                        if (sortParams.sortKey && sortParams.sortOrder) {
                            Object.assign(nextQuery, sortParams);
                        } else {
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                        }
                        return nextQuery;
                    });
                }}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <Typography.Title className="mb-4 mt-0" level={5}>
                            船运单列表
                        </Typography.Title>

                        <Space className="mb-4" wrap>
                            <Input
                                aria-label="货主/编号关键词"
                                placeholder="请输入货主名称或编号搜索"
                                value={keyword}
                                onChange={event => setKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="码头关键词"
                                placeholder="请输入码头关键词搜索"
                                value={portKeyword}
                                onChange={event => setPortKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="船舶关键词"
                                placeholder="请输入船舶关键词搜索"
                                value={shipKeyword}
                                onChange={event => setShipKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="船务专员关键词"
                                placeholder="请输入船务专员搜索"
                                value={handlerName}
                                onChange={event => setHandlerName(event.target.value)}
                            />
                            <Select
                                aria-label="船运单类型"
                                allowClear
                                placeholder="请选择类型"
                                value={type}
                                options={Object.entries(typeLabels).map(([value, label]) => ({
                                    value: Number(value),
                                    label
                                }))}
                                onChange={value => setType(value)}
                            />
                            <Select
                                aria-label="船运单状态"
                                allowClear
                                placeholder="请选择状态"
                                value={state}
                                options={Object.entries(stateLabels).map(([value, label]) => ({
                                    value: Number(value),
                                    label
                                }))}
                                onChange={value => setState(value)}
                            />
                            <ZhDateRangePicker
                                clearable
                                endPlaceholder="创建结束日期"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="创建开始日期"
                                type="daterange"
                                value={[beginTime, endTime]}
                                valueFormat="YYYY-MM-DD"
                                onChange={updateCreatedTimeRange}
                            />
                            <Button type="primary" onClick={submitSearch}>
                                查询
                            </Button>
                        </Space>

                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    total > 0 ? (
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={20}
                                total={total}
                                hideOnSinglePage
                                onChange={nextPage => setPage(nextPage)}
                            />
                        </div>
                    ) : null
                }
            />
        </section>
    );
}
